import type { ReactNode } from 'react';

/** Нижняя полоса меню в `BlueHeader`: те же отступы и шаг, что у `BlueHeaderTabStrip`. */
export function embedClientBlueHeaderFooterNavClass(): string {
  return [
    'flex w-full min-w-0 flex-wrap items-center gap-1.5 px-2',
    'py-0.5'
  ].join(' ');
}

/** Класс для `NavLink` внутри синей шапки (контур client mini-site). */
export function embedClientBlueHeaderNavLinkClass({ isActive }: { isActive: boolean }): string {
  const base =
    'shrink-0 rounded-full px-3 py-2 text-[11px] font-semibold leading-tight tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-fg)]';
  return isActive
    ? `${base} bg-[var(--accent-fg)] text-[var(--accent-bg)] shadow-sm`
    : `${base} border border-[color-mix(in_srgb,var(--accent-fg)_45%,transparent)] text-[var(--accent-fg)] hover:border-[var(--accent-fg)]`;
}

export type BlueHeaderProps = {
  title: string;
  subtitle?: string;
  /** Кнопка «назад» или меню слева от заголовка */
  leading?: ReactNode;
  /** Нижняя зона: горизонтальные pill-табы */
  footer?: ReactNode;
  className?: string;
};

/**
 * Синяя зона шапки (прототип виджета): фон accent-bg, текст accent-fg.
 * Используется в мобильной оболочке и при необходимости в других контурах.
 */
export function BlueHeader({ title, subtitle, leading, footer, className = '' }: BlueHeaderProps) {
  return (
    <header className={`bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-[var(--shadow-card)] ${className}`}>
      <div className="flex min-h-[44px] items-start gap-2 px-3 pt-2.5 pb-1">
        {leading ? <div className="shrink-0 pt-0.5">{leading}</div> : null}
        <div className={`min-w-0 flex-1 text-center ${leading ? 'pr-8' : ''}`}>
          <p className="truncate text-[15px] font-bold leading-tight tracking-tight">{title}</p>
          {subtitle ? (
            <p className="mt-0.5 truncate text-xs font-medium text-[var(--accent-fg)]">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {footer ? (
        <div className="border-t border-[color-mix(in_srgb,var(--accent-fg)_18%,transparent)] pb-2.5 pt-2">{footer}</div>
      ) : null}
    </header>
  );
}

export type BlueNavTabItem = {
  label: string;
  active: boolean;
  onSelect: () => void;
};

/** Pill-табы внутри синей зоны: неактивные — полупрозрачный белый фон, активный — белый фон + цвет accent. */
export function BlueHeaderTabStrip({ items, 'aria-label': ariaLabel = 'Разделы' }: { items: BlueNavTabItem[]; 'aria-label'?: string }) {
  return (
    <div
      className="flex max-w-full gap-1.5 overflow-x-auto px-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
      aria-label={ariaLabel}
    >
      {items.map((item, i) => (
        <button
          key={i}
          type="button"
          role="tab"
          aria-selected={item.active}
          onClick={item.onSelect}
          className={`shrink-0 rounded-full px-3.5 py-2 text-[11px] font-semibold leading-tight tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-fg)] active:scale-[0.98] ${
            item.active
              ? 'bg-[var(--accent-fg)] text-[var(--accent-bg)] shadow-sm'
              : 'border border-[color-mix(in_srgb,var(--accent-fg)_45%,transparent)] text-[var(--accent-fg)] hover:border-[var(--accent-fg)]'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
